/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { FeatureRollout } from '@ms/yammer-data/dist/state/ecsFeatureRollouts/types';
import { Experiment } from '@ms/yammer-data/dist/state/types';
import {
  useCanCurrentUserAccessAdminSettings,
  useShowStorylineAdminSettings,
} from '@ms/yammer-hooks/dist/adminSettings';
import { useIsAskMeAnythingEnabled, useIsCommunityAskMeAnythingEnabled } from '@ms/yammer-hooks/dist/askMeAnything';
import { useCanAccessCampaignsRecommendations, useIsCampaignSearchEnabled } from '@ms/yammer-hooks/dist/campaignRights';
import { useShouldShowCuratedThreadLists } from '@ms/yammer-hooks/dist/curatedThreadList';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useIsBespokeCommunityCreateEditEnabled, useIsNewGroupFilesEnabled } from '@ms/yammer-hooks/dist/groups';
import { useAreLeadershipManagementFlightsEnabled } from '@ms/yammer-hooks/dist/leaderSettings';
import {
  useCanUserAccessLeaderDirectory,
  useCanUserAccessLeadershipCorner,
} from '@ms/yammer-hooks/dist/leadershipCorner';
import {
  LegacyCreateGroup,
  LegacyDiscoverGroups,
  LegacyGroupDelete,
  LegacyGroupFiles,
  LegacyGroupInfo,
  LegacyGroupInsights,
  LegacyGroupSettings,
  LegacyNetworkAdminSettings,
  LegacyUserSettings,
} from '@ms/yammer-web-components/dist/route/legacyRoutes';
import {
  adminSettingsLeadershipIdentificationUrl,
  adminSettingsManageLeaderUrl,
  leaderSettingsManageLeaderPage,
  searchPathCampaigns,
  searchPathFiles,
  searchPathGroups,
  searchPathInbox,
  searchPathQuestions,
  searchPathThreads,
  searchPathTopics,
  searchPathUsers,
  storylineAdminSettingsUrl,
} from '@ms/yammer-web-support/dist/urls';
import React, { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import PageRoot from '../PageRoot';

import {
  AdminSettings,
  AskMeAnything,
  AskMeAnythingCommunityEvents,
  AskMeAnythingEventCreation,
  AskMeAnythingEventEditing,
  AskMeAnythingLeadersEvents,
  AskMeAnythingUserEvents,
  Campaign,
  CampaignRouter,
  CampaignsDashboard,
  CampaignsHub,
  CuratedThreadList,
  DelegationSettings,
  DiscoverGroups,
  Feed,
  Group,
  GroupCampaigns,
  GroupSettings,
  Inbox,
  InsightsMain,
  LeaderDashboard,
  LeadersCuratedFeed,
  LeadersDirectory,
  LeadershipCorner,
  LegacyRouteConverter,
  LegacyRouter,
  NetworkThemeThreads,
  PendingPosts,
  QuestionThread,
  Questions,
  Search,
  StorylineAdminAdvancedSettings,
  StorylineAdminSettings,
  StorylineHome,
  StorylineRouter,
  Thread,
  Topic,
  TopicNetworkQuestion,
  TopicNetworkQuestionBrowse,
  UnifiedTopic,
  UsagePolicy,
  User,
  UserAchievements,
} from './loadableComponents';

export const ContentRoutes: FC = () => {
  const areLeadershipManagementFlightsEnabled = useAreLeadershipManagementFlightsEnabled();
  const canAccessAdminPage = useCanCurrentUserAccessAdminSettings();
  const canAccessCampaignPage = useCanAccessCampaignsRecommendations();
  const canUserAccessLeaderDirectory = useCanUserAccessLeaderDirectory();
  const canUserAccessLeadershipCorner = useCanUserAccessLeadershipCorner();
  const isAskMeAnythingEnabled = useIsAskMeAnythingEnabled();
  const isCampaignSearchEnabled = useIsCampaignSearchEnabled();
  const isCommunityAskMeAnythingEnabled = useIsCommunityAskMeAnythingEnabled();
  const isLeadershipCuratedFeedEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaLeadershipCuratedFeed);
  const isNetworkQuestionsEnabledInGA = useExperimentTreatmentWithoutReporting(Experiment.AnswersInVivaGA) === true;
  const isNewGroupFilesEnabled = useIsNewGroupFilesEnabled();
  const isStorylineAdminEnabled = useShowStorylineAdminSettings();
  const isStorylineHomeEnabled = useIsFeatureRolloutEnabled(FeatureRollout.StorylineHome);
  const isBespokeCommunityCreateEditEnabled = useIsBespokeCommunityCreateEditEnabled();
  const isUnifiedTopicsEnabled = useExperimentTreatmentWithoutReporting(Experiment.UnifiedTopicsFeed) === 'enabled';
  const topicsPageComponent = isUnifiedTopicsEnabled ? UnifiedTopic : Topic;
  const isFAQifyEnabled = useShouldShowCuratedThreadLists();

  const askMeAnythingPages = isAskMeAnythingEnabled
    ? [
        <Route
          key="AskMeAnything"
          exact={true}
          path={[
            '/ama/:teamsMeetingId',
            '/ama/:teamsMeetingId/in-review',
            '/ama/:teamsMeetingId/published',
            '/ama/:teamsMeetingId/dismissed',
          ]}
          component={AskMeAnything}
        />,
        <Route
          key="AskMeAnythingUserEvents"
          exact={true}
          path="/users/:userId/events/askmeanything"
          component={AskMeAnythingUserEvents}
        />,
        <Route
          key="AskMeAnythingLeadersEvents"
          exact={true}
          path="/leadership-corner/events/askmeanything"
          component={AskMeAnythingLeadersEvents}
        />,
      ]
    : null;

  const askMeAnythingCommunityEventsPage = isCommunityAskMeAnythingEnabled ? (
    <Route path="/groups/:groupId/events/askmeanything" component={AskMeAnythingCommunityEvents} />
  ) : null;

  const leadershipCorner = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leadership-corner" component={LeadershipCorner} />
  ) : null;

  const leadersCuratedFeed =
    canUserAccessLeadershipCorner && isLeadershipCuratedFeedEnabled
      ? [
          <Route
            key="LeadersCuratedFeed"
            exact={true}
            path="/leadership-corner/featured-conversations"
            component={LeadersCuratedFeed}
          />,
        ]
      : null;

  const leaderDashboard = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leader-dashboard" component={LeaderDashboard} />
  ) : null;

  const legacyGroupFilesPage = !isNewGroupFilesEnabled ? (
    <Route exact={true} path="/groups/:groupId/files" component={LegacyGroupFiles} />
  ) : null;

  const leadersDirectoryPages = canUserAccessLeaderDirectory
    ? [
        <Route key="Leaders" exact={true} path="/leaders" component={LeadersDirectory} />,
        <Route key="LeadersList" exact={true} path="/leaders/:subRoute" component={LeadersDirectory} />,
      ]
    : null;

  const storylineHome = isStorylineHomeEnabled ? (
    <Route exact={true} path="/storylines" component={StorylineHome} />
  ) : null;

  const leaderSettingPages = areLeadershipManagementFlightsEnabled
    ? [
        <Redirect
          exact={true}
          key="UserSettingsLeaders"
          from="/user-settings/leaders"
          to={adminSettingsLeadershipIdentificationUrl}
        />,
        <Redirect
          exact={true}
          key="UserSettingsLeadersManage"
          from={leaderSettingsManageLeaderPage}
          to={adminSettingsManageLeaderUrl}
        />,
      ]
    : null;

  const storylineAdminAdvancedSettingsPage = isStorylineAdminEnabled ? (
    <Route exact={true} path="/admin/settings/storyline/advanced" component={StorylineAdminAdvancedSettings} />
  ) : null;

  const userAchievementsRoute = isNetworkQuestionsEnabledInGA ? (
    <Route exact={true} path="/achievements/:userId" component={UserAchievements} />
  ) : null;

  const adminSettingsPages = canAccessAdminPage ? (
    <Route key="AdminSettings" path="/admin" component={AdminSettings} />
  ) : null;

  const searchCampaignRoute =
    isCampaignSearchEnabled && canAccessCampaignPage ? (
      <Route exact={true} path={searchPathCampaigns} component={Search} />
    ) : null;

  const searchAnswerRoute = isNetworkQuestionsEnabledInGA ? (
    <Route exact={true} path={searchPathQuestions} component={Search} />
  ) : null;

  const curatedThreadListRoute = isFAQifyEnabled
    ? [
        <Route key="CuratedThreadList" exact={true} path="/curated-thread-list/" component={CuratedThreadList} />,
        <Route
          key="CuratedThreads"
          exact={true}
          path="/curated-thread-list/:curatedThreadListId/:sourceFileId?"
          component={CuratedThreadList}
        />,
      ]
    : null;

  return (
    <Switch>
      <Route exact={true} path="/ama/new/:userId?" component={AskMeAnythingEventCreation} />
      <Route exact={true} path="/ama/:teamsMeetingId/edit" component={AskMeAnythingEventEditing} />
      {askMeAnythingPages}
      <Route exact={true} path="/feed" component={Feed} />
      {storylineHome}
      <Route exact={true} path="/users/:userId/pending-posts/:subRoute?" component={PendingPosts} />
      <Route exact={true} path="/inbox" component={Inbox} />
      <Route exact={true} path="/inbox/:inboxFeedSubRoute" component={Inbox} />
      <Route key="CampaignsDashboard" exact={true} path="/admin/campaigns-dashboard" component={CampaignsDashboard} />,
      <Route
        key="CampaignsDashboardSubRoute"
        exact={true}
        path="/admin/campaigns-dashboard/:campaignsDashboardSubRoute"
        component={CampaignsDashboard}
      />
      <Route key="CampaignsHub" exact={true} path="/campaigns-hub" component={CampaignsHub} />
      <Route exact={true} path="/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/groups/:groupId" component={Group} />
      <Route exact={true} key="GroupCampaigns" path="/groups/:groupId/campaigns" component={GroupCampaigns} />
      <Route
        exact={true}
        path="/groups/:groupId/settings"
        component={isBespokeCommunityCreateEditEnabled ? GroupSettings : LegacyGroupSettings}
      />
      <Route exact={true} path="/groups/:groupId/delete" component={LegacyGroupDelete} />
      <Route exact={true} path="/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/groups/:groupId/insights" component={LegacyGroupInsights} />
      {legacyGroupFilesPage}
      <Route exact={true} path="/groups/:groupId/:groupFeedSubRoute" component={Group} />
      {askMeAnythingCommunityEventsPage}
      <Route exact={true} path="/campaigns/:campaignId" component={Campaign} />
      <Route exact={true} path="/threads/:threadId/:topLevelMessageId?/:secondLevelMessageId?" component={Thread} />
      <Route exact={true} path="/users/:userId" component={User} />
      {userAchievementsRoute}
      <Route exact={true} path="/users/:userId/:userFeedSubRoute" component={User} />
      <Route exact={true} path="/topics/:topicId" component={topicsPageComponent} />
      <Route exact={true} path="/topics/:topicId/:topicFeedSubRoute" component={topicsPageComponent} />
      <Route exact={true} path="/usage-policy" component={UsagePolicy} />
      <Route exact={true} path="/user-settings" component={LegacyUserSettings} />
      <Route exact={true} path="/user-settings/delegates" component={DelegationSettings} />
      <Route exact={true} path={storylineAdminSettingsUrl} component={StorylineAdminSettings} />
      {storylineAdminAdvancedSettingsPage}
      {leaderSettingPages}
      <Route exact={true} path="/network-admin" component={LegacyNetworkAdminSettings} />
      <Route exact={true} path="/discover-groups" component={DiscoverGroups} />
      <Route exact={true} path="/discover-groups/:discoverGroupsSubRoute" component={DiscoverGroups} />
      <Route exact={true} path={searchPathThreads} component={Search} />
      <Route exact={true} path={searchPathGroups} component={Search} />
      <Route exact={true} path={searchPathUsers} component={Search} />
      <Route exact={true} path={searchPathFiles} component={Search} />
      <Route exact={true} path={searchPathTopics} component={Search} />
      <Route exact={true} path={searchPathInbox} component={Search} />
      {searchCampaignRoute}
      {searchAnswerRoute}
      {leadershipCorner}
      {leaderDashboard}
      {leadersDirectoryPages}
      {leadersCuratedFeed}
      <Route
        key="Question"
        exact={true}
        path="/questions/:threadId/:topLevelMessageId?/:secondLevelMessageId?"
        component={QuestionThread}
      />
      <Route key="Questions" exact={true} path="/questions" component={Questions} />
      <Route
        key="TopicNetworkQuestionBrowse"
        exact={true}
        path="/topics-network-question/browse"
        component={TopicNetworkQuestionBrowse}
      />
      <Route
        key="TopicNetworkQuestionFeed"
        exact={true}
        path="/topics-network-question/:topicId"
        component={TopicNetworkQuestion}
      />
      <Route
        key="TopicNetworkQuestionFeedSubroute"
        exact={true}
        path="/topics-network-question/:topicId/:topicFeedSubRoute"
        component={TopicNetworkQuestion}
      />
      {adminSettingsPages}
      <Route exact={false} path="/insights" component={InsightsMain} />
      <Route exact={true} path="/:networkPermaLink/legacy/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/:networkPermaLink/legacy/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/:networkPermaLink/legacy/groups" component={LegacyDiscoverGroups} />
      <Route exact={true} path="/:networkPermaLink/users/:userId/storyline" component={StorylineRouter} />
      <Route exact={true} path="/:networkPermaLink/campaigns/:campaignId" component={CampaignRouter} />
      <Route exact={true} path="/:networkPermaLink/:entityType/:entityId/legacy/*" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/:entityId" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/show" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/inGroup" component={LegacyRouter} />
      <Route exact={true} path="/theme-threads/:content" component={NetworkThemeThreads} />
      {curatedThreadListRoute}
      <Route exact={true} path="/" component={PageRoot} />
      <Route exact={false} path="*" component={LegacyRouteConverter} />
    </Switch>
  );
};

export default ContentRoutes;
